import i18next from 'i18next';
import { nanoid } from 'nanoid';
import { isEmpty, pick, noop, omit, isNil } from 'lodash/fp';
import { QUERY_BUILDER_TYPE } from 'app/store/__generated__/types';
import { navigateToQueryBuilder } from 'app/routes';
import { TransformationType } from 'app/lib/types/server';
import { toStringFqn } from 'app/lib/utils';
import { identifier } from 'app/lib/utils/sql_generator.js';
import { doSelectWorkbenchDatasetTab } from 'app/store/preferences/actions';
import { doSelectTab } from 'app/store/findDatasets/actions.js';
import { selectView } from 'app/store/view/selectors.js';
import { selectProject, selectProjectUid } from 'app/store/projects/selectors.js';
import { selectEntityByPropId } from 'app/store/entities/selectors.js';
import { doLogToBackend } from 'app/store/log/actions';
import { VERSION } from 'app/store/exploreData/constants';
import {
    doCreateTransformation,
    refreshProject,
    refreshProjectData
} from 'app/store/operations/actions.js';
import { selectOperationInputEntity } from 'app/store/operations/selectors.js';
import { QueryState } from 'app/lib/constants';
import {
    doCancelQuery,
    doDeleteExploreQuery,
    doExecuteExploreQuery,
    doExecuteSqlQuery
} from 'app/store/snowflakeQueries/actions.js';
import { selectQueryForPropQueryUuid } from 'app/store/snowflakeQueries/selectors.js';
import {
    LOAD_QUERIES_FOR_ENTITY,
    CREATE_QUERY,
    UPDATE_QUERY,
    DELETE_QUERY,
    REFRESH_QUERY,
    SELECT_CELL,
    CHOOSE_VIZ,
    EXPLORATION_DOWNLOAD,
    INITIATE_EXPLORATION_DOWNLOAD,
    EXPLORATION_DOWNLOAD_INITIATED,
    OPEN_EXPLORE,
    CLOSE_EXPLORE,
    PREVIEW_EXPLORATION,
    CLOSE_QUERY_TAB,
    EDIT_QUERY,
    UPDATE_EXPLORE_TRANSFORM,
    PROMOTE_EXPLORATION,
    CREATE_EXPLORE_TRANSFORM,
    COPY_EXPLORATIONS,
    GET_DIMENSION_FILTER_VALUES,
    SET_DIMENSION_FILTER_VALUES,
    RENAME_QUERY
} from './types.js';
import {
    selectNextAvailableIndexForEntityId,
    selectQueryByUuid,
    selectQueryForPropEntityIdQueryUuid,
    selectQueryForPropEntityIdAndQueryUuid
} from './selectors.js';
import { buildSql } from './utils.js';

export const doLoadQueriesForEntity = ({ viewUuid, fqn }) => ({
    type: LOAD_QUERIES_FOR_ENTITY,
    api: { version: 2, name: 'ListExplorations' },
    params: {
        ...(viewUuid && { transformationId: viewUuid }),
        ...(fqn && { tableFqn: fqn })
    }
});

export const doChooseViz = ({ entityId, queryUuid, viz }) => ({
    type: CHOOSE_VIZ,
    payload: { entityId, queryUuid, viz },
    effect: ({ store }) => {
        const state = store.getState();
        const query = selectQueryByUuid(state, { entityId, queryUuid });
        const entity = selectEntityByPropId(state, { entityId });

        store.dispatch(
            doLogToBackend('exploration', 'visualization', {
                type: viz,
                model: query?.model,
                entityId,
                queryUuid,
                projectName: entity?.spaceName,
                viewName: entity?.name,
                source: entity?.tableFqn
            })
        );
    }
});

const doDeleteQuery = ({ entityId, queryUuid }) => ({
    type: DELETE_QUERY,
    api: { version: 2, name: 'DeleteExploration' },
    params: { id: queryUuid },
    meta: { entityId }
});

export const doCloseQueryTab = ({ entityId, queryUuid }) => ({
    type: CLOSE_QUERY_TAB,
    effect: ({ store }) => {
        const state = store.getState();
        const query = selectQueryForPropQueryUuid(state, { queryUuid });

        if (query) {
            if (query.state === QueryState.RUNNING) {
                store.dispatch(doCancelQuery({ id: query.queryId }));
            }

            store.dispatch(doDeleteExploreQuery({ queryUuid }));
        }

        store.dispatch(doDeleteQuery({ entityId, queryUuid }));
    }
});

export function initializeQueryModel(queryUuid, table, index) {
    return {
        version: VERSION,
        queryUuid,
        table,
        index,
        limit: null,
        filters: [],
        sorts: [],
        dimensions: null,
        measures: [],
        summaryFilters: []
    };
}

const filterUnusedDimensionFilters = (model) => {
    if (!model.dimensions) {
        return model;
    }

    const dimensionColumns = model.dimensions.map(({ column }) => column.name);

    return {
        ...model,
        dimensionFilters: pick(dimensionColumns, model.dimensionFilters)
    };
};

const nameExploration = (model) =>
    model.name ?? i18next.t('queries:explore.name', { index: model.index });
const describeExploration = (fqnString, name) =>
    i18next.t('queries:explore.describe', { name: `${fqnString} / ${name}` });
const describeProjectExploration = (projectName, viewName, name) =>
    i18next.t('queries:explore.describe', {
        name: `${projectName} / ${viewName} / ${name}`
    });

const handleSaveProjectExplorationSuccess =
    (spaceId, entityId, queryUuid, onSucceeded, keepOpen) =>
    ({ dispatch, getState, params }) => {
        const state = getState();
        const view = selectView(state);
        const project = selectProject(state);

        dispatch(
            doExecuteExploreQuery({
                spaceId,
                entityId,
                queryUuid,
                description: describeProjectExploration(
                    project.name,
                    view.name,
                    nameExploration(params.model)
                ),
                pathname: window.location.pathname
            })
        );

        if (!keepOpen) {
            dispatch(doSelectWorkbenchDatasetTab(queryUuid, entityId));
        }

        onSucceeded(queryUuid);
    };

const handleSaveExplorationSuccess =
    (tableFqn, queryUuid, skipRowCount, onSucceeded, keepOpen) =>
    ({ dispatch, params }) => {
        dispatch(
            doExecuteExploreQuery({
                entityId: toStringFqn(tableFqn),
                queryUuid,
                description: describeExploration(
                    toStringFqn(tableFqn),
                    nameExploration(params.model)
                ),
                pathname: window.location.pathname,
                skipRowCount
            })
        );

        if (!keepOpen) {
            dispatch(doSelectTab(queryUuid));
        }

        onSucceeded(queryUuid);
    };

export const doCreateExploration = ({
    tableFqn,
    queryUuid = nanoid(),
    model: currentModel,
    skipRowCount = false,
    keepOpen = false,
    onSucceeded = noop
}) => ({
    type: CREATE_QUERY,
    api: { version: 2, name: 'CreateExploration' },
    meta: { entityId: toStringFqn(tableFqn), queryUuid, keepOpen },
    params: (state) => {
        const index = selectNextAvailableIndexForEntityId(state, {
            entityId: toStringFqn(tableFqn)
        });
        const previousModel = initializeQueryModel(queryUuid, toStringFqn(tableFqn), index);
        let model = {
            ...previousModel,
            ...currentModel,
            skipRowCount
        };
        model = filterUnusedDimensionFilters(model);

        return {
            id: queryUuid,
            tableFqn,
            model,
            sql: buildSql(toStringFqn(tableFqn), model)
        };
    },
    onSucceeded: handleSaveExplorationSuccess(
        tableFqn,
        queryUuid,
        skipRowCount,
        onSucceeded,
        keepOpen
    )
});

export const doUpdateExploration = ({
    tableFqn,
    queryUuid,
    model: currentModel,
    keepOpen = false,
    onSucceeded = noop
}) => ({
    type: UPDATE_QUERY,
    api: { version: 2, name: 'UpdateExploration' },
    meta: { entityId: toStringFqn(tableFqn), queryUuid, keepOpen },
    params: (state) => {
        const query = selectQueryForPropEntityIdAndQueryUuid(state, {
            queryUuid,
            entityId: toStringFqn(tableFqn)
        });
        let model = {
            ...query.model,
            ...currentModel
        };
        model = filterUnusedDimensionFilters(model);

        return {
            id: queryUuid,
            tableFqn,
            model,
            sql: buildSql(toStringFqn(tableFqn), model)
        };
    },
    onSucceeded: handleSaveExplorationSuccess(tableFqn, queryUuid, false, onSucceeded, keepOpen)
});

export const doCreateProjectExploration = ({
    spaceId,
    viewUuid,
    queryUuid = nanoid(),
    model: currentModel,
    keepOpen = false,
    onSucceeded = noop
}) => ({
    type: CREATE_QUERY,
    api: { version: 2, name: 'CreateExploration' },
    meta: { entityId: viewUuid, queryUuid, keepOpen },
    params: (state) => {
        const view = selectView(state);
        const index = selectNextAvailableIndexForEntityId(state, { entityId: viewUuid });
        const previousModel = initializeQueryModel(queryUuid, view.name, index);
        let model = {
            ...previousModel,
            ...currentModel
        };
        model = filterUnusedDimensionFilters(model);

        return {
            id: queryUuid,
            transformationId: viewUuid,
            model,
            sql: buildSql(identifier(view?.name), model)
        };
    },
    onSucceeded: handleSaveProjectExplorationSuccess(
        spaceId,
        viewUuid,
        queryUuid,
        onSucceeded,
        keepOpen
    )
});

export const doUpdateProjectExploration = ({
    spaceId,
    viewUuid,
    queryUuid,
    model: currentModel,
    keepOpen = false,
    onSucceeded = noop
}) => ({
    type: UPDATE_QUERY,
    api: { version: 2, name: 'UpdateExploration' },
    meta: { entityId: viewUuid, queryUuid, keepOpen },
    params: (state) => {
        const query = selectQueryForPropEntityIdAndQueryUuid(state, {
            queryUuid,
            entityId: viewUuid
        });
        const view = selectView(state);
        let model = {
            ...query.model,
            ...currentModel
        };
        model = filterUnusedDimensionFilters(model);

        return {
            id: queryUuid,
            transformationId: viewUuid,
            model,
            sql: buildSql(identifier(view?.name), model)
        };
    },
    onSucceeded: handleSaveProjectExplorationSuccess(
        spaceId,
        viewUuid,
        queryUuid,
        onSucceeded,
        keepOpen
    )
});

export const doCopyExplorations = ({ spaceId, viewUuid, model }) => ({
    type: COPY_EXPLORATIONS,
    meta: { viewUuid },
    effect: ({ store }) =>
        store.dispatch(
            doCreateProjectExploration({ spaceId, viewUuid, model: omit('queryUuid', model) })
        )
});

export const doRefreshQuery = ({ entityId, queryUuid }) => ({
    type: REFRESH_QUERY,
    effect: ({ store }) => {
        const state = store.getState();
        const spaceId = selectProjectUid(state);
        const view = selectView(state);
        const project = selectProject(state);
        const query = selectQueryForPropEntityIdAndQueryUuid(state, { queryUuid, entityId });
        const description = isNil(spaceId)
            ? describeExploration(entityId, nameExploration(query.model))
            : describeProjectExploration(project.name, view.name, nameExploration(query.model));

        store.dispatch(
            doExecuteExploreQuery({
                spaceId,
                entityId,
                queryUuid,
                pathname: window.location.pathname,
                description,
                skipRowCount: query.model.skipRowCount
            })
        );
    }
});

export const doOpenExplore = ({ exploreMode, dialogMode, queryUuid, fqn } = {}) => ({
    type: OPEN_EXPLORE,
    payload: { queryUuid, exploreMode: exploreMode ?? dialogMode, fqn }
});

export const doCloseExplore = () => ({
    type: CLOSE_EXPLORE
});

export const doPreviewExploration = ({ model, fqn, viewName }) => ({
    type: PREVIEW_EXPLORATION,
    effect: ({ store }) => {
        const state = store.getState();
        const tableName = fqn ? toStringFqn(fqn) : identifier(viewName);
        const sql = buildSql(tableName, model);
        store.dispatch(
            doExecuteSqlQuery({
                queryKey: 'explore.preview',
                sql,
                spaceUid: selectProjectUid(state),
                limit: 100,
                description: i18next.t('queries:explore.preview')
            })
        );
    }
});

/** @deprecated Use doOpenExplore */
export const doOpenDialog = doOpenExplore;
/** @deprecated Use doCloseExplore */
export const doCloseDialog = doCloseExplore;

export const doRenameExploration = ({ viewUuid, tableFqn, queryUuid, name }) => ({
    type: RENAME_QUERY,
    api: { version: 2, name: 'UpdateExploration' },
    params: (state) => {
        const query = selectQueryForPropEntityIdAndQueryUuid(state, {
            entityId: viewUuid ?? toStringFqn(tableFqn),
            queryUuid
        });

        return {
            id: queryUuid,
            ...(viewUuid && { transformationId: viewUuid }),
            ...(tableFqn && { tableFqn }),
            model: { ...query.model, name },
            sql: query.sql
        };
    }
});

export const doSelectCell = ({ entityId, queryUuid, columnIndex, rowIndex }) => ({
    type: SELECT_CELL,
    payload: { entityId, queryUuid, columnIndex, rowIndex }
});

const doDownloadExplorationInitiated = (viewName) => ({
    type: EXPLORATION_DOWNLOAD_INITIATED,
    payload: { viewName }
});

const doInitiateExplorationDownloadQuery = ({ sql, spaceUid, viewName, targetName, fqn }) => ({
    type: INITIATE_EXPLORATION_DOWNLOAD,
    api: { version: 2, name: 'InitiateExplorationDownload' },
    params: {
        spaceUid,
        sql,
        fqn
    },
    meta: {
        viewName,
        targetName
    },
    onProgress: ({ dispatch, responseName }) => {
        if (responseName === 'ExplorationDownloadInitiated') {
            dispatch(doDownloadExplorationInitiated(targetName));
        }
    }
});

export const doInitiateExplorationDownload = (fqn, queryUuid) => ({
    type: EXPLORATION_DOWNLOAD,
    effect: ({ store }) => {
        const state = store.getState();
        const view = selectView(state);
        const targetName = 'Exploration';
        const query = selectQueryByUuid(state, { queryUuid, fqn });

        const sql = buildSql(view?.name ? identifier(view.name) : toStringFqn(fqn), query.model);
        store.dispatch(
            doInitiateExplorationDownloadQuery({
                sql,
                spaceUid: view?.spaceUid,
                viewName: view?.name ?? toStringFqn(fqn),
                targetName,
                fqn
            })
        );
    }
});

export const doEditQuery = (entityId) => ({
    type: EDIT_QUERY,
    payload: { entityId }
});

function doCreateQueryTransformation({ model, keepOpen }) {
    return doCreateTransformation({
        actionType: CREATE_EXPLORE_TRANSFORM,
        transformationType: TransformationType.QUERY,
        keepOpen,
        onCreated: keepOpen
            ? (params) => {
                  const { entityId, spaceSlug, viewSlug } = params.payload.viewRef;
                  params.dispatch(doEditQuery(entityId));
                  navigateToQueryBuilder(spaceSlug, viewSlug);
                  refreshProjectData(params);
              }
            : undefined,
        payload: (state) => {
            const source = selectOperationInputEntity(state) ?? selectView(state);
            return {
                model,
                sql: buildSql(identifier(source.name), model),
                sourceViews: [source.uid]
            };
        }
    });
}

function doUpdateTransformation({ entityId, model, keepOpen }) {
    return {
        type: UPDATE_EXPLORE_TRANSFORM,
        api: { version: 2, name: 'UpdateTransformationNode' },
        params: (state) => {
            const [projectId, viewId] = entityId.split('.');
            const entity = selectEntityByPropId(state, { entityId });
            const source = entity.sources[0];
            const spaceUid = projectId;
            const viewUid = viewId;

            return {
                payload: {
                    _type: QUERY_BUILDER_TYPE,
                    model,
                    sql: buildSql(identifier(source.name), model),
                    sourceViews: [source.uid]
                },
                spaceUid,
                viewUid
            };
        },
        onSucceeded: keepOpen ? refreshProjectData : refreshProject
    };
}

export function doSaveTransformation({ entityId, model, keepOpen }) {
    return isEmpty(entityId)
        ? doCreateQueryTransformation({ model, keepOpen })
        : doUpdateTransformation({ entityId, model, keepOpen });
}

export const doPromoteExploration = ({ entityId, queryUuid }) => ({
    type: PROMOTE_EXPLORATION,
    effect: ({ store }) => {
        const state = store.getState();
        const query = selectQueryForPropEntityIdQueryUuid(state, { entityId, queryUuid });

        const model = {
            ...query.model,
            dimensionFilters: []
        };

        store.dispatch(doSaveTransformation({ model }));
    }
});

export const doGetDimensionFilterValues = ({ queryUuid, name, sql }) => ({
    type: GET_DIMENSION_FILTER_VALUES,
    effect: ({ store }) => {
        const state = store.getState();
        const spaceUid = selectProjectUid(state);
        store.dispatch(
            doExecuteSqlQuery({
                queryKey: `metrics.dimensionFilters.byQueryUuid[${queryUuid}][${name}]`,
                sql,
                spaceUid,
                fullData: true,
                description: i18next.t('queries:explore.dimensionFilters')
            })
        );
    }
});

export const doSetDimensionFilterValues = ({ entityId, fqn, queryUuid, name, values }) => ({
    type: SET_DIMENSION_FILTER_VALUES,
    payload: { entityId, queryUuid, name, values },
    effect: ({ store }) => {
        const state = store.getState();
        const spaceId = selectProjectUid(state);
        const query = selectQueryForPropQueryUuid(state, { queryUuid });

        if (fqn) {
            store.dispatch(
                doUpdateExploration({
                    queryUuid,
                    tableFqn: fqn,
                    model: query.model
                })
            );
        } else {
            store.dispatch(
                doUpdateProjectExploration({
                    queryUuid,
                    viewUuid: entityId,
                    spaceId,
                    model: query.model
                })
            );
        }
    }
});
